// Per-kind payload validation for the message builders. Splitting this out
// of `message-builders.ts` keeps the builder entry under the LOC ceiling and
// isolates the Zod-schema dispatch as its own testable unit.

import { z } from "zod";
import {
  MessageKind,
  audioPayloadSchema,
  calendarPayloadSchema,
  callPayloadSchema,
  emailPayloadSchema,
  filePayloadSchema,
  gifPayloadSchema,
  imagePayloadSchema,
  linkOnlyPayloadSchema,
  mediaGroupPayloadSchema,
  systemPayloadSchema,
  terminalPayloadSchema,
  videoPayloadSchema,
} from "./message-payload";

/**
 * Validate per-kind payload. Unrecognized payload for a payload-less kind
 * (e.g. `text`, `image`) is preserved opaquely — callers that want strict
 * per-kind rejection should validate at their boundary.
 */
export function validatePayload(
  kind: MessageKind,
  payload: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (payload == null) return null;
  switch (kind) {
    case "calendar":
      return parseOrNull(calendarPayloadSchema, payload);
    case "email":
      return parseOrNull(emailPayloadSchema, payload);
    case "terminal_output":
    case "terminal_snapshot":
    case "terminal_live":
    case "terminal_recording":
    case "run":
    case "command_history":
      return parseOrNull(terminalPayloadSchema, payload);
    case "call_started_video":
    case "call_started_audio":
    case "call_ended":
      return parseOrNull(callPayloadSchema, payload);
    case "media_group":
      return parseOrNull(mediaGroupPayloadSchema, payload);
    case "link_only":
      return parseOrNull(linkOnlyPayloadSchema, payload);
    case "system":
      return parseOrNull(systemPayloadSchema, payload);
    case "image":
      return parseOrNull(imagePayloadSchema, payload);
    case "video":
      return parseOrNull(videoPayloadSchema, payload);
    case "audio":
      return parseOrNull(audioPayloadSchema, payload);
    case "file":
      return parseOrNull(filePayloadSchema, payload);
    case "gif":
      return parseOrNull(gifPayloadSchema, payload);
    case "text":
      return payload;
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      return payload;
    }
  }
}

function parseOrNull<T>(
  schema: z.ZodType<T>,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(
      `Invalid message payload: ${parsed.error.issues
        .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
        .join("; ")}`,
    );
  }
  // Keep the payload object shape — schemas are .passthrough() for most kinds
  // which means unknown fields survive the parse.
  return parsed.data as Record<string, unknown>;
}
